import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [lockedOut, setLockedOut] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLockedOut(false);
    setLoading(true);
    try {
      await login(email, password, remember);
      navigate("/", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = detail?.code;
      if (status === 402 && code === "payment_required" && detail?.payment_token) {
        // Self-signup user who never completed the paywall — send them
        // straight to /pay/:token to activate.
        toast.info("Complete your 150 USDT payment to activate your account.");
        navigate(`/pay/${detail.payment_token}`, { replace: true });
        return;
      }
      if (status === 429) {
        // Too many failed attempts — surface a helpful CTA to the forgot-password flow
        setError("");
        setLockedOut(true);
      } else if (err?.code === "ECONNABORTED" || err?.message === "Network Error") {
        setError("Connection issue — please check your internet and try again.");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(t("login.resetSent"));
      setForgotOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-ink-0 text-white relative z-10">
      {/* Left visual */}
      <div className="hidden lg:block relative overflow-hidden border-r border-white/5">
        <img
          src="https://images.unsplash.com/photo-1778849122862-7fa43a7200f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBtaW5pbWFsJTIwdGV4dHVyZSUyMHByZW1pdW18ZW58MHx8fHwxNzg0NTU0NDkwfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,11,0.4) 0%, rgba(10,10,11,0.95) 100%)" }} />
        <div className="relative h-full flex flex-col justify-between p-14">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-3xl font-semibold tracking-tight">EM</span>
            <span className="font-display text-3xl font-light tracking-tight text-gold">Operating System</span>
          </div>
          <div className="max-w-md">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{t("login.brandTagline")}</p>
            <h1 className="font-display text-4xl font-light tracking-tight leading-tight text-white">
              {t("login.brandHeadline")}
            </h1>
            <p className="mt-6 text-zinc-400 text-sm leading-relaxed">
              {t("login.brandSub")}
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6 z-10">
          <LanguageSwitcher />
        </div>
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-baseline gap-2 mb-10">
            <span className="font-display text-2xl font-semibold">EM</span>
            <span className="font-display text-2xl font-light text-gold">OS</span>
          </div>

          {!forgotOpen ? (
            <>
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">{t("login.eyebrow")}</p>
              <h2 className="font-display text-3xl font-light tracking-tight mb-10">
                {t("login.welcomeBack")}<span className="text-gold">.</span>
              </h2>

              <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">{t("login.email")}</label>
                  <input
                    data-testid="login-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="em-input"
                    placeholder={t("login.emailPlaceholder")}
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">{t("login.password")}</label>
                  <div className="relative">
                    <input
                      data-testid="login-password"
                      type={showPw ? "text" : "password"}
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="em-input pr-11"
                      placeholder={t("login.passwordPlaceholder")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      aria-label="Toggle password"
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 text-zinc-400 cursor-pointer select-none">
                    <input
                      data-testid="login-remember"
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="w-4 h-4 rounded border-white/10 bg-ink-0 accent-gold"
                    />
                    <span>{t("login.rememberMe")}</span>
                  </label>
                  <button
                    type="button"
                    data-testid="forgot-password-btn"
                    onClick={() => setForgotOpen(true)}
                    className="text-gold hover:text-gold-hover"
                    style={{ transitionProperty: "color", transitionDuration: "160ms" }}
                  >
                    {t("login.forgot")}
                  </button>
                </div>

                {error && (
                  <div data-testid="login-error" className="text-sm text-red-400 bg-red-900/20 border border-red-900/40 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}

                {lockedOut && (
                  <div data-testid="login-locked" className="text-sm bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-3">
                    <p className="text-amber-200 font-medium mb-1">Too many failed attempts</p>
                    <p className="text-amber-100/80 text-xs leading-relaxed mb-3">
                      For your security we&apos;ve temporarily paused sign-in for this account. The fastest way back in is to reset your password &mdash; the lock lifts automatically.
                    </p>
                    <button
                      type="button"
                      data-testid="locked-forgot-btn"
                      onClick={() => { setForgotEmail(email); setForgotOpen(true); setLockedOut(false); }}
                      className="text-sm text-gold hover:text-gold-hover font-semibold"
                      style={{ transitionProperty: "color", transitionDuration: "160ms" }}
                    >
                      Reset password &rarr;
                    </button>
                  </div>
                )}

                <button
                  data-testid="login-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold text-ink-0 font-semibold rounded-lg px-6 py-3 hover:bg-gold-hover disabled:opacity-50 flex items-center justify-center gap-2 group"
                  style={{ transitionProperty: "background-color, box-shadow", transitionDuration: "180ms" }}
                >
                  <span>{loading ? t("login.signingIn") : t("login.signIn")}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5" style={{ transitionProperty: "transform", transitionDuration: "180ms" }} />
                </button>
              </form>

              <p className="mt-10 text-xs text-zinc-600 text-center">
                {t("login.provisioned")}
              </p>
            </>
          ) : (
            <>
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">{t("login.resetTitle")}</p>
              <h2 className="font-display text-3xl font-light tracking-tight mb-10">
                {t("login.forgotHeading")}<span className="text-gold">?</span>
              </h2>
              <form onSubmit={submitForgot} className="space-y-5">
                <input
                  data-testid="forgot-email"
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="em-input"
                  placeholder={t("login.forgotEmailPlaceholder")}
                />
                <button
                  type="submit"
                  data-testid="forgot-submit"
                  className="w-full bg-gold text-ink-0 font-semibold rounded-lg px-6 py-3 hover:bg-gold-hover"
                  style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
                >
                  {t("login.sendResetLink")}
                </button>
                <button
                  type="button"
                  onClick={() => setForgotOpen(false)}
                  className="w-full text-sm text-zinc-400 hover:text-white"
                >
                  {t("login.backToSignIn")}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
